<template>
  <div class="container box">
    <b-loading :is-full-page="true" v-model="isLoading" :can-cancel="false"></b-loading>
    
    <div class="notes-management">
      <h1 class="is-size-4 mt-3 mb-5">Manage Company Notes</h1>
      <p class="notification">Add custom messaging to guide and inform travelers across booking pages. 
        Customize what your travelers see with targeted messages for announcements, policy reminders, 
        booking guidance, or important updates.</p>

      <!-- Tab Navigation -->
      <div class="tabs">
        <ul class="m-0">
          <li 
            v-for="section in sections" 
            :key="section.key"
            :class="{ 'is-active': activeSection === section.key }"
            @click="setActiveSection(section.key)"
          >
            <a>
              <!--<span class="icon is-small">
                <b-icon :icon="section.icon" size="is-small"></b-icon>
              </span> -->
              <span>{{ section.name }}</span>
              <span 
                v-if="notes[section.key] && notes[section.key].isActive" 
                class="tag is-success is-small ml-2"
              >
                <b-icon icon="eye-outline" size="is-small"></b-icon>
              </span>
              <span 
                v-else
                class="tag is-gray is-small ml-2"
              >
                <b-icon icon="eye-off-outline" size="is-small"></b-icon>
              </span>
            </a>
          </li>
        </ul>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <div class="content-header mb-5">
          <div class="level">
            <div class="level-left">
              <div class="level-item">
                <div>
                  <h2 class="is-size-5 mb-1">{{ getCurrentSection().name }}</h2>
                  <p class="has-text-grey">{{ getCurrentSection().description }}</p>
                </div>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item">
                
              </div>
            </div>
          </div>
        </div>

        <!-- Manage active or not -->
        <div class="mb-5">
          <div class="field">
            <label class="label">Visibility</label>
            <b-switch 
              v-model="currentNote.isActive"
              :rounded="true"
              type="is-success"
              @input="onToggleChange"
            >
              {{ currentNote.isActive ? 'Published' : 'Hidden' }}
            </b-switch>
          </div>
        </div>

        <!-- Editor Section -->
        <div class="editor-section">
          <div class="field">
            <label class="label">Message Content</label>
            <div class="control">
              <RichTextEditor 
                v-model="currentNote.content"
                @input="onContentChange"
              />
            </div>
            <!-- <p class="help">
              Use the toolbar above to format your message. HTML tags are supported.
            </p> -->
          </div>
        </div>

        <!-- Preview Section -->
        <!-- <div class="preview-section mt-5" v-if="currentNote.content">
          <label class="label">Preview</label>
          <div class="preview-box">
            <div class="notes-preview" v-html="currentNote.content"></div>
          </div>
        </div> -->

        <!-- <div v-if="currentNote.isActive" class="notification mt-5">
          This note will be shown to all travelers on the {{ getCurrentSection().name }} page.
        </div> -->

        <!-- Actions -->
        <div class="actions mt-5">
          <div class="field is-grouped">
            <div class="control">
              <b-button 
                @click="saveNote" 
                type="is-gant" 
                :loading="isSaving"
                :disabled="!hasChanges"
              >
                Save Changes
              </b-button>
            </div>
            <div class="control">
              <b-button 
                @click="resetNote" 
                type="is-light"
                :disabled="!hasChanges"
              >
                Reset
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RichTextEditor from '@/components/UI/RichTextEditor.vue'
import NotesService from '@/services/NotesService.js'

export default {
  name: 'CompanyNotesManagement',
  components: {
    RichTextEditor
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      activeSection: 'home',
      hasChanges: false,
      originalNotes: {},
      notesService: null,
      notes: {
        home: {
          content: '',
          isActive: false
        },
        airSearch: {
          content: '',
          isActive: false
        },
        hotelSearch: {
          content: '',
          isActive: false
        },
        carSearch: {
          content: '',
          isActive: false
        },
        checkout: {
          content: '',
          isActive: false
        }
      },
      sections: [
        {
          key: 'home',
          name: 'Home',
          icon: 'home',
          description: 'Main page banner message'
        },
        {
          key: 'airSearch',
          name: 'Air Search',
          icon: 'airplane',
          description: 'Flight search page banner message'
        },
        {
          key: 'hotelSearch',
          name: 'Hotel Search',
          icon: 'bed',
          description: 'Hotel search page banner message'
        },
        {
          key: 'carSearch',
          name: 'Car Search',
          icon: 'car',
          description: 'Car rental search page banner message'
        },
        {
          key: 'checkout',
          name: 'Checkout',
          icon: 'credit-card',
          description: 'Checkout page banner message'
        }
      ]
    }
  },
  computed: {
    currentNote() {
      return this.notes[this.activeSection] || { content: '', isActive: false }
    },
    currentTravellerProfile() {
      return this.$store.getters['travellersAndTrips/getCurrentTravellerProfile']
    }
  },
  mounted() {
    this.notesService = new NotesService(this.$app)
    this.loadNotes()
  },
  methods: {
    getCurrentSection() {
      return this.sections.find(s => s.key === this.activeSection) || this.sections[0]
    },
    setActiveSection(sectionKey) {
      if (this.hasChanges) {
        this.$buefy.dialog.confirm({
          title: 'Unsaved Changes',
          message: 'You have unsaved changes. Do you want to discard them?',
          confirmText: 'Discard',
          type: 'is-warning',
          hasIcon: true,
          onConfirm: () => {
            this.resetNote()
            this.activeSection = sectionKey
          }
        })
      } else {
        this.activeSection = sectionKey
      }
    },
    onToggleChange() {
      this.checkForChanges()
    },
    onContentChange() {
      this.checkForChanges()
    },
    checkForChanges() {
      const original = this.originalNotes[this.activeSection] || { content: '', isActive: false }
      const current = this.currentNote
      
      this.hasChanges = (
        original.content !== current.content ||
        original.isActive !== current.isActive
      )
    },
    async loadNotes() {
      this.isLoading = true
      try {
        const result = await this.notesService.getAllNotes(this.currentTravellerProfile.account.id)

        if (result && result.data && result.data.length > 0) {
          // Get the first account record (should only be one for the current account)
          const accountData = result.data[0]

          if (accountData && accountData.notes) {
            // Transform API response to component format and update store cache
            accountData.notes.forEach(note => {
              if (this.notes[note.section]) {
                this.notes[note.section] = {
                  content: note.content || '',
                  isActive: note.isActive || false,
                  id: note._id || accountData._id // Use note ID or fallback to account ID
                }
              }
            })

            // Update the store cache with the loaded notes
            this.$store.dispatch('travellersAndTrips/setAccountNotes', accountData.notes)
          }
        }

        // Store original state for change detection
        this.originalNotes = JSON.parse(JSON.stringify(this.notes))

      } catch (error) {
        console.error('Error loading notes:', error)
        // For development - show a warning but don't block the UI
        this.$buefy.toast.open({
          message: 'Notes API not yet available. Using demo mode.',
          type: 'is-warning'
        })
        // Initialize with empty notes for demo
        this.originalNotes = JSON.parse(JSON.stringify(this.notes))
      } finally {
        this.isLoading = false
      }
    },
    async saveNote() {
      this.isSaving = true
      try {
        const noteData = {
          section: this.activeSection,
          content: this.currentNote.content,
          isActive: this.currentNote.isActive,
          accountId: this.currentTravellerProfile.account.id
        }

        const result = await this.notesService.saveNote(noteData)

        if (result && !result.errors) {
          // Update the note with returned ID if it's a new note
          if (result.data && result.data.id) {
            this.notes[this.activeSection].id = result.data.id
          }

          // Update original state
          this.originalNotes[this.activeSection] = JSON.parse(JSON.stringify(this.currentNote))
          this.hasChanges = false

          // Update the store cache with the new note data
          this.$store.dispatch('travellersAndTrips/updateAccountNote', {
            section: this.activeSection,
            content: this.currentNote.content,
            isActive: this.currentNote.isActive
          })

          this.$buefy.toast.open({
            message: 'Note saved successfully!',
            type: 'is-success'
          })
        } else {
          throw new Error(result.errors ? result.errors[0].title : 'Save failed')
        }
      } catch (error) {
        console.error('Error saving note:', error)
        // For development - simulate successful save
        this.$buefy.toast.open({
          message: 'Note saved (demo mode - API not yet available)',
          type: 'is-warning'
        })
        // Update original state for demo
        this.originalNotes[this.activeSection] = JSON.parse(JSON.stringify(this.currentNote))
        this.hasChanges = false
      } finally {
        this.isSaving = false
      }
    },
    resetNote() {
      const original = this.originalNotes[this.activeSection] || { content: '', isActive: false }
      this.notes[this.activeSection] = JSON.parse(JSON.stringify(original))
      this.hasChanges = false
    }
  }
}
</script>

<style lang="scss" scoped>
.notes-management {
  .tabs {
    margin-bottom: 0;

    ul {
      border-bottom: 1px solid #dbdbdb;
    }

    li {
      cursor: pointer;

      &.is-active a {
        border-color: #a4393a;
        color: #a4393a;
      }
    }
  }

  .tab-content {
    padding: 2rem 0;
  }

  .content-header {
    border-bottom: 1px solid #f5f5f5;
    padding-bottom: 1rem;
  }

  .editor-section {
    .label {
      font-weight: 600;
      color: #363636;
    }
  }

  .preview-section {
    .preview-box {
      border: 1px solid #dbdbdb;
      border-radius: 4px;
      padding: 1rem;
      background-color: #fafafa;
      min-height: 100px;
    }

    .notes-preview {
      font-size: 14px;
      line-height: 1.5;

      // Style the preview to match the actual notes display
      p {
        margin-bottom: 0.5rem;

        &:last-child {
          margin-bottom: 0;
        }
      }

      strong {
        font-weight: 600;
      }

      em {
        font-style: italic;
      }

      ul, ol {
        margin-left: 1rem;
        margin-bottom: 0.5rem;
      }
    }
  }

  .actions {
    border-top: 1px solid #f5f5f5;
    padding-top: 1rem;
  }
}
</style>
